import React from "react";
import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import TabPanel from "@mui/lab/TabPanel";
import { retrievePausedOrders } from "./selector";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { Order, OrderItem, OrderUpdateInput } from "../../../lib/types/order";
import { Product } from "../../../lib/types/product";
import { Messages, serverApi } from "../../../lib/config";
import { sweetErrorHandling } from "../../../lib/sweetAlert";
import { OrderStatus, PaymentStatus } from "../../../lib/enums/order.enum";
import { useGlobals } from "../../hooks/useGlobals";
import OrderService from "../../services/OrderService";
import { T } from "../../../lib/types/common";
import { Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import useDeviceDetect from "../../hooks/useDeviceDetect";

/** REDUX SLICE & SELECTOR */

const pausedOrdersRetriever = createSelector(
  retrievePausedOrders,
  (pausedOrders) => ({ pausedOrders })
);

interface PausedOrderProps {
  setValue: (input: string) => void;
}

export default function PausedOrders(props: PausedOrderProps) {
  const { setValue } = props;
  const { authMember, setOrderBulder, authTable } = useGlobals();
  const { pausedOrders } = useSelector(pausedOrdersRetriever);
  const device = useDeviceDetect();

  /** HANDLERS **/
  const deleteOrderHandler = async (e: T) => {
    try {
      if (!authTable) throw new Error(Messages.error2);
      const orderId = e.target.value;
      const input: OrderUpdateInput = {
        orderId: orderId,
        orderStatus: OrderStatus.CANCELLED,
      };

      const confirmation = window.confirm("Do you want to delete the order?");
      if (confirmation) {
        const order = new OrderService();
        await order.updateOrder(input);
        setOrderBulder(new Date());
      }
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  const processOrderHandler = async (e: T) => {
    try {
      if (!authMember && !authTable) throw new Error(Messages.error2);
      //  PAYMENT PROCESS

      const orderId = e.target.value;
      const input: OrderUpdateInput = {
        orderId: orderId,
        orderStatus: OrderStatus.PENDING,
      };
      if (authMember) input.paymentStatus = PaymentStatus.PAID;

      const confirmation = window.confirm(
        authMember
          ? "Do you want to proceed with payment?"
          : "Do you want to order?"
      );
      if (confirmation) {
        const order = new OrderService();
        await order.updateOrder(input);
        setValue("2");
        setOrderBulder(new Date());
      }
    } catch (err) {
      console.log(err);
      sweetErrorHandling(err).then();
    }
  };

  if (device === "mobile") {
    return (
      <TabPanel value="1">
        <Box className="mobile-orders-list">
          {pausedOrders && pausedOrders.length > 0 ? (
            pausedOrders.map((order: Order) => (
              <Box key={order._id} className="mobile-order-card">
                {/* Order Items */}
                <Box className="mobile-order-items">
                  {order.orderItems?.map((item: OrderItem) => {
                    const product: Product = order.productData.filter(
                      (ele: Product) => item.productId === ele._id
                    )[0];
                    if (!product) return null;
                    const imagePath = `${serverApi}/${product.productImages[0]}`;
                    return (
                      <Box key={item._id} className="mobile-order-item">
                        <img src={imagePath} className="mobile-order-item-img" alt={product.productName} />
                        <Box className="mobile-order-item-info">
                          <Typography className="mobile-order-item-name">
                            {product.productName}
                          </Typography>
                          <Box className="mobile-order-item-price-row">
                            <Typography className="mobile-order-item-price">
                              ${item.itemPrice}
                            </Typography>
                            <Typography className="mobile-order-item-quantity">
                              x {item.itemQuantity}
                            </Typography>
                            <Typography className="mobile-order-item-total">
                              ${(item.itemQuantity * item.itemPrice).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {/* Order Summary */}
                <Box className="mobile-order-summary">
                  <Box className="mobile-order-summary-row">
                    <Typography>Product price</Typography>
                    <Typography>${(order.orderTotal - order.orderDelivery).toFixed(2)}</Typography>
                  </Box>
                  <Box className="mobile-order-summary-row">
                    <Typography>Delivery cost</Typography>
                    <Typography>${order.orderDelivery.toFixed(2)}</Typography>
                  </Box>
                  <Box className="mobile-order-summary-total">
                    <Typography>Total</Typography>
                    <Typography>${order.orderTotal.toFixed(2)}</Typography>
                  </Box>
                </Box>

                {/* Action Buttons */}
                <Box className="mobile-order-actions">
                  <Button
                    value={order._id}
                    variant="outlined"
                    className="mobile-order-cancel-btn"
                    startIcon={<DeleteIcon />}
                    onClick={deleteOrderHandler}
                  >
                    Cancel
                  </Button>
                  <Button
                    value={order._id}
                    variant="contained"
                    className="mobile-order-process-btn"
                    startIcon={<ShoppingCartIcon />}
                    onClick={processOrderHandler}
                  >
                    {authMember ? "Payment" : "Order"}
                  </Button>
                </Box>
              </Box>
            ))
          ) : (
            <Box className="mobile-no-orders">
              <img src="/icons/noimage-list.svg" alt="No orders" />
              <Typography>No paused orders</Typography>
            </Box>
          )}
        </Box>
      </TabPanel>
    );
  }

  return (
    <TabPanel value="1">
      <Stack>
        {pausedOrders?.map((order: Order) => {
          return (
            <Box key={order._id} className="order-main-box">
              <Box className="order-box-scroll">
                {order.orderItems?.map((item: OrderItem) => {
                  const product: Product = order.productData.filter(
                    (ele: Product) => item.productId === ele._id
                  )[0];
                  const imagePath = `${serverApi}/${product.productImages[0]}`;
                  return (
                    <Box key={item._id} className="orders-name-price">
                      <img src={imagePath} className="order-dish-img" />
                      <p className="title-dish">{product.productName}</p>
                      <Box className="price-box">
                        <p>${item.itemPrice}</p>
                        <img src={"/icons/close.svg"} />
                        <p>{item.itemQuantity}</p>
                        <img src="/icons/pause.svg" />
                        <p>${item.itemQuantity * item.itemPrice}</p>
                      </Box>
                    </Box>
                  );
                })}
                <Box className="total-price-box">
                  <Box className="box-total">
                    <p>Product price</p>
                    <p>${order.orderTotal - order.orderDelivery}</p>
                    <img src={"/icons/plus.svg"} />
                    <p>delivery cost</p>
                    <p>${order.orderDelivery}</p>
                    <img src={"/icons/pause.svg"} />
                    <p>Total</p>
                    <p>${order.orderTotal}</p>
                  </Box>
                  <Button
                    value={order._id}
                    variant="contained"
                    className="cancel-button"
                    color={"secondary"}
                    onClick={deleteOrderHandler}
                  >
                    cancel
                  </Button>
                  <Button
                    value={order._id}
                    variant="contained"
                    className="verify-button"
                    onClick={processOrderHandler}
                  >
                    {authMember ? "payment" : "order"}
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}

        {!pausedOrders ||
          (pausedOrders.length === 0 && (
            <Box
              width={"800px"}
              display="flex"
              flexDirection="row"
              justifyContent="center"
            >
              <img
                src={"/icons/noimage-list.svg"}
                style={{ width: 400, height: 400 }}
              />
            </Box>
          ))}
      </Stack>
    </TabPanel>
  );
}
